/**
 * api.ts — the single HTTP layer for CME Studio's two backends, with Auth0
 * token attachment. Every surface hook builds on this; nothing else should
 * call `fetch` directly.
 *
 * IMPORTANT — token audience per backend. The two backends each have their
 * OWN Auth0 API audience (see config.ts):
 *   - cmeinsight  → config.auth0Audience   (tender domain API, B1b)
 *   - wikiLLM     → config.wikiLLMAudience  (Knowledge Base service, B1a)
 * The `Auth0Provider` boots with only the tender audience, so a token for the
 * KB service must be minted on demand via
 * `getAccessTokenSilently({ authorizationParams: { audience } })`. `useApi()`
 * therefore exposes one client per backend, each pre-bound to the right
 * audience. (See wiki: Frontend_action_contract.md, B1b_backend_build_plan.md.)
 *
 * Every request is bounded by REQUEST_TIMEOUT_MS — EXCEPT file uploads, which
 * are exempt because their duration is the user's uplink, not the server's
 * health. `fetch` never times out on its own, and a promise that never settles
 * is invisible to every error path we have. See `resolveTimeout` below.
 */
import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";

import { config } from "./config";

/** Thrown for every non-2xx response and for transport/token failures. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** Plain-Italian message for any thrown error, safe to show a non-technical user. */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return "Errore imprevisto durante la chiamata al server.";
}

type QueryParams = Record<string, string | number | boolean | undefined>;

interface RequestOptions {
  signal?: AbortSignal;
  params?: QueryParams;
  /** JSON body — serialized and Content-Type set automatically. */
  body?: unknown;
  /** Multipart body — sent as-is so the browser sets the boundary. */
  formData?: FormData;
  /**
   * Deadline for THIS call, in ms. Omit for the default (REQUEST_TIMEOUT_MS, or
   * none when `formData` is present — see below); pass `null` to wait forever.
   */
  timeoutMs?: number | null;
}

type TokenGetter = (audience: string) => Promise<string>;

/**
 * Hard ceiling on any single request. `fetch` has NO default timeout, so without
 * this a hung connection (dropped Wi-Fi, a proxy holding the socket open) leaves
 * the returned promise pending forever — not an error, just silence. Every
 * caller that releases state on settlement then hangs with it: TanStack mutations
 * stay `isPending`, and the Strategia row's optimistic lease
 * (StrategyTable.tsx) would freeze on un-saved values with no way back.
 * A timeout turns that into an ordinary rejection every existing error path
 * already handles. 30s is comfortably above the slowest normal call (AI quote
 * extraction and analyze runs are async jobs, not held-open requests).
 */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * How long `request` waits before aborting. A multipart body gets NO deadline.
 *
 * An upload's duration is set by the file size and the user's uplink, not by the
 * server's responsiveness: the backend accepts up to 1 GB
 * (tender-api/src/tender_api/api/documents.py), and a 50 MB drawings PDF over an
 * ordinary Italian FTTC uplink (1–20 Mbit/s up) already exceeds 30s. `fetch`
 * exposes no upload-progress event, so there is no way to tell "slow but
 * healthy" from "stalled" — any finite ceiling would truncate somebody's real
 * file, non-deterministically (fine on office fibre, broken at home), and blame
 * the server for a client-side abort.
 *
 * Keyed off the request SHAPE rather than opted out at each call site on
 * purpose: a new upload added later is exempt by construction instead of
 * silently inheriting a 30s cap. An explicit `timeoutMs` still overrides this.
 */
function resolveTimeout(options: RequestOptions): number | null {
  if (options.timeoutMs !== undefined) return options.timeoutMs;
  return options.formData ? null : REQUEST_TIMEOUT_MS;
}

function buildUrl(baseUrl: string, path: string, params?: QueryParams): string {
  const url = new URL(`${baseUrl.replace(/\/+$/, "")}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function request<T>(
  baseUrl: string,
  audience: string,
  getToken: TokenGetter,
  method: string,
  path: string,
  options: RequestOptions,
): Promise<T> {
  let token: string;
  try {
    token = await getToken(audience);
  } catch (cause) {
    // auth0-spa-js errors carry `.error` (e.g. login_required, consent_required,
    // invalid_audience) + `.error_description`. Surface them so token failures
    // are diagnosable instead of hidden behind a generic message.
    const code =
      cause && typeof cause === "object" && "error" in cause
        ? String((cause as { error: unknown }).error)
        : undefined;
    const desc =
      cause && typeof cause === "object" && "error_description" in cause
        ? String((cause as { error_description: unknown }).error_description)
        : cause instanceof Error
          ? cause.message
          : undefined;
    throw new ApiError(
      401,
      `Token non disponibile per il backend${code ? ` [${code}]` : ""}${
        desc ? `: ${desc}` : ""
      }. Effettua di nuovo l'accesso.`,
      cause,
    );
  }

  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  let bodyInit: BodyInit | undefined;
  if (options.formData) {
    bodyInit = options.formData;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    bodyInit = JSON.stringify(options.body);
  }

  // Our own controller so the deadline can fire regardless of whether the caller
  // supplied a signal (TanStack passes one for queries, never for mutations).
  // The caller's signal is relayed into it so cancellation still works.
  const controller = new AbortController();
  const timeoutMs = resolveTimeout(options);
  let timedOut = false;
  const timer =
    timeoutMs === null
      ? null
      : setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs);
  const relayAbort = (): void => controller.abort();
  options.signal?.addEventListener("abort", relayAbort);

  /**
   * The deadline can fire during the fetch OR while the body is being streamed,
   * and both must surface as the same classified, Italian-language error — a
   * bare AbortError has no `.status` and is not an `instanceof Error` in every
   * runtime, so `apiErrorMessage` would degrade it to "Errore imprevisto".
   */
  const deadlineError = (cause: unknown): ApiError =>
    new ApiError(
      408,
      `Il server non ha risposto entro ${(timeoutMs ?? 0) / 1000} secondi. Riprova.`,
      cause,
    );

  try {
    let res: Response;
    try {
      res = await fetch(buildUrl(baseUrl, path, options.params), {
        method,
        headers,
        body: bodyInit,
        signal: controller.signal,
      });
    } catch (cause) {
      // A deadline hit is not the same story as an unreachable backend: the
      // service answered the TCP connect and then went quiet.
      if (timedOut) throw deadlineError(cause);
      // Transport-level failure (backend down, CORS, DNS) — distinct from an HTTP error.
      throw new ApiError(
        0,
        "Backend non raggiungibile. Verifica che il servizio sia in esecuzione.",
        cause,
      );
    }

    if (!res.ok) {
      const body = await safeJson(res); // swallows its own parse/abort failure
      if (timedOut) throw deadlineError(null);
      const detail =
        typeof body === "object" && body !== null && "detail" in body
          ? String((body as { detail: unknown }).detail)
          : res.statusText || `Errore HTTP ${res.status}`;
      throw new ApiError(res.status, detail, body);
    }

    if (res.status === 204) return undefined as T;
    try {
      return (await res.json()) as T;
    } catch (cause) {
      // Headers arrived, then the body stalled (deadline) or was malformed.
      // Classified here rather than left to propagate raw, so every caller sees
      // an ApiError whatever stage the request died at.
      if (timedOut) throw deadlineError(cause);
      throw new ApiError(0, "Risposta del server illeggibile.", cause);
    }
  } finally {
    // Cleared only once the BODY has been read too — aborting the controller
    // also aborts the response stream, so a server that sends headers and then
    // stalls mid-body hits the same deadline and the same 408 above.
    if (timer !== null) clearTimeout(timer);
    options.signal?.removeEventListener("abort", relayAbort);
  }
}

export interface BackendClient {
  get: <T>(path: string, options?: RequestOptions) => Promise<T>;
  post: <T>(path: string, options?: RequestOptions) => Promise<T>;
  put: <T>(path: string, options?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, options?: RequestOptions) => Promise<T>;
  del: <T>(path: string, options?: RequestOptions) => Promise<T>;
}

function makeClient(baseUrl: string, audience: string, getToken: TokenGetter): BackendClient {
  return {
    get: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "GET", path, options),
    post: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "POST", path, options),
    put: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "PUT", path, options),
    patch: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "PATCH", path, options),
    del: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "DELETE", path, options),
  };
}

export interface Api {
  /** Knowledge Base service (wiki-llm-service, B1a — live). */
  wikiLLM: BackendClient;
  /** Tender domain API (CMEInsight backend, B1b — not built yet). */
  cmeinsight: BackendClient;
}

/**
 * Returns one HTTP client per backend, each pre-bound to the correct Auth0
 * audience and the current user's token getter. Stable across renders.
 */
export function useApi(): Api {
  const { getAccessTokenSilently } = useAuth0();
  return useMemo(() => {
    const getToken: TokenGetter = (audience) =>
      getAccessTokenSilently({ authorizationParams: { audience } });
    return {
      wikiLLM: makeClient(config.wikiLLMApiUrl, config.wikiLLMAudience, getToken),
      cmeinsight: makeClient(config.cmeinsightApiUrl, config.auth0Audience, getToken),
    };
  }, [getAccessTokenSilently]);
}
